import * as moment from "moment";
import {JobAppointmentStatusEventDataApiDto, JobEventApiDto} from "../../../api/model/rest";
import AppointmentStatus from "../../appointment/AppointmentStatus";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import JobEvent, {JobEventTypeEnum} from "./JobEvent";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import {IconColor} from "../../../hiberUiKit/src/molecules/StreamItem/StreamItemIcon";
import DateUtils from "../../../utils/DateUtils";

/**
 * Job appointment status changed event.
 */
export default class JobAppointmentStatusEvent extends JobEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} jobId job id.
	 * @param {JobEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber job reference number.
	 * @param {JobAppointmentStatusEventData} data job appointment status change event data.
	 */
	constructor(public readonly id: string,
		public readonly jobId: string,
		public readonly type: JobEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: JobAppointmentStatusEventData) {
		super(id, jobId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: JobEventApiDto): JobAppointmentStatusEvent {
		const data = JobAppointmentStatusEventData.fromJson(json.data as JobAppointmentStatusEventDataApiDto);
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new JobAppointmentStatusEvent(json.id, json.jobId.toString(), JobEventTypeEnum.AppointmentStatusChanged,
			entity, moment(json.createdAt), json.referenceNumber, data);
	}

	statusDisplayText() {
		return this.data.status.label.toLocaleLowerCase()
	}

	newStatusDisplayText() {
		return this.data.nextStatus.label.toLocaleLowerCase()
	}

	getIcon() {
		return Icon.Appointments;
	}

	getIconColor(): IconColor {
		return IconColor.Burgundy;
	}

	getActivityType(): string {
		return `APPOINTMENT`;
	}

	getActivityMessage(): string {
		return `Status changed from ${this.statusDisplayText().capitalize()}
				to ${this.newStatusDisplayText().capitalize()}
				for ${this.data.appointmentDate.format(DateUtils.DATE_TIME_FORMAT)}`;
	}
}

export class JobAppointmentStatusEventData {
	constructor(public readonly id: string,
		public readonly appointmentDate: moment.Moment,
		public readonly status: AppointmentStatus,
		public readonly nextStatus: AppointmentStatus) {}

	public static fromJson(json: JobAppointmentStatusEventDataApiDto): JobAppointmentStatusEventData {
		return new JobAppointmentStatusEventData(json.id, moment(json.date), AppointmentStatus.fromString(json.previousStatus),
			AppointmentStatus.fromString(json.currentStatus));
	}
}